import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCopy, faLink, faPlus, faSpinner, faBoxArchive, faPenToSquare, faQrcode, faChartPie, faEye,
} from "@fortawesome/free-solid-svg-icons";
import { useAccountType, useAgencyView } from "../hooks/useAccountType";
import { ppapiPost, apiErrorMessage } from "../utils/partnerApi";

/**
 * Referral & Campaigns (KOL_CAMPAIGNS_AGENCY_PLAN.md §2, §10).
 * - Default (bare) links for app / trade / website.
 * - Campaign links: one code per campaign, `&campaign=<code>` added to every link.
 * Data lives in CMS MySQL `sym_api_partner_campaigns` via ppapi
 * (listCampaigns / createCampaign / updateCampaign / archiveCampaign).
 */

const MAX_LABEL = 60;
const MAX_ACTIVE = 50;

// The three surfaces every link must work on (§2). `app.` + website read
// `?affiliate=`, `trade.` reads `?ref=`; `campaign` is additive on all three.
export const LINK_SURFACES = [
    { key: "app", label: "Swap app", host: "https://app.instaswap.com/", param: "affiliate" },
    { key: "trade", label: "Trade (spot & perps)", host: "https://trade.instaswap.com/", param: "ref" },
    { key: "site", label: "Website", host: "https://instaswap.com/", param: "affiliate" },
];

export function buildLink(surface, affiliateId, code) {
    const u = new URL(surface.host);
    u.searchParams.set(surface.param, affiliateId || "");
    if (code) u.searchParams.set("campaign", code);
    return u.toString();
}

const copyText = (text, what = "Link") => {
    navigator.clipboard?.writeText(text)
        .then(() => toast.success(`${what} copied`))
        .catch(() => toast.error("Could not copy — select and copy manually."));
};

const modalStyles = {
    overlay: { backgroundColor: "rgba(6, 7, 7, 0.78)", zIndex: 2000 },
    content: {
        top: "50%", left: "50%", right: "auto", bottom: "auto", transform: "translate(-50%, -50%)",
        width: "90%", maxWidth: 520, maxHeight: "92vh", padding: 0, background: "transparent", border: "none", overflow: "auto",
    },
};

const LinkRow = ({ url, compact }) => (
    <div className="rfLinkRow" style={compact ? { margin: 0 } : undefined}>
        <div className="rfLink" style={compact ? { fontSize: 12.5, padding: "8px 12px" } : undefined}>
            <FontAwesomeIcon className="ic" icon={faLink} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{url}</span>
        </div>
        <button type="button" className="refCopy" onClick={() => copyText(url)}>
            <FontAwesomeIcon icon={faCopy} /> Copy
        </button>
    </div>
);

const LinksBlock = ({ affiliateId, code, compact = false }) => (
    <div style={{ display: "grid", gap: compact ? 8 : 12 }}>
        {LINK_SURFACES.map((s) => (
            <div key={s.key}>
                <div className="fldLbl" style={{ marginBottom: 4, fontSize: compact ? 11.5 : undefined }}>{s.label}</div>
                <LinkRow url={buildLink(s, affiliateId, code)} compact={compact} />
            </div>
        ))}
    </div>
);

const CampaignForm = ({ initial, onSubmit, onCancel, submitting }) => {
    const [label, setLabel] = useState(initial?.label || "");
    const [active, setActive] = useState(initial ? Number(initial.active) !== 0 : true);
    const isEdit = !!initial;
    const submit = () => {
        const l = label.trim();
        if (!l) { toast.error("Give the campaign a name."); return; }
        if (l.length > MAX_LABEL) { toast.error(`Name must be ${MAX_LABEL} characters or less.`); return; }
        onSubmit(isEdit ? { code: initial.code, label: l, active: active ? 1 : 0 } : { label: l });
    };
    return (
        <div className="iswModal" style={{ maxWidth: "none" }}>
            <h2 className="mTitle">{isEdit ? "Edit campaign" : "New campaign"}</h2>
            <div className="fld" style={{ marginTop: 16 }}>
                <label className="fldLbl">Campaign name</label>
                <input
                    className="input" type="text" value={label} maxLength={MAX_LABEL} autoFocus
                    onChange={(e) => setLabel(e.target.value)} placeholder="e.g. YouTube review — October"
                    onKeyDown={(e) => { if (e.key === "Enter" && !submitting) submit(); }}
                />
                <p className="fldHelp" style={{ marginTop: 8 }}>
                    Only you see the name. The link gets a short code that never changes — orders keep it forever.
                </p>
            </div>
            {isEdit && (
                <label style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 14px", fontSize: 13.5, color: "#cdd5cf", cursor: "pointer" }}>
                    <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                    Active — paused campaigns keep their history but new orders are booked as “Direct”.
                </label>
            )}
            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button type="button" className="btnPrimary" onClick={submit} disabled={submitting}>
                    {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : (isEdit ? "Save" : "Create campaign")}
                </button>
                <button type="button" className="btnGhost" onClick={onCancel} disabled={submitting}>Cancel</button>
            </div>
        </div>
    );
};

const Referral = () => {
    const accountType = useAccountType();
    const agencyView = useAgencyView();
    const readOnly = accountType === "agency" && !!agencyView;
    // In agency view the links must carry the VIEWED partner's id (listCampaigns echoes it).
    const [viewedAffiliateId, setViewedAffiliateId] = useState(null);
    const affiliateId = (readOnly && viewedAffiliateId) || localStorage.getItem("affiliateId") || "";

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [unavailable, setUnavailable] = useState(false); // ppapi without the campaigns actions yet
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [expanded, setExpanded] = useState(null); // code with links open
    const [qr, setQr] = useState(null); // url shown as QR
    const [showArchived, setShowArchived] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        ppapiPost("listCampaigns", {}, { view: true })
            .then((data) => {
                // A ppapi without the action answers with its default payload (no `campaigns`) → hide the block.
                if (!Array.isArray(data?.campaigns)) { setUnavailable(true); return; }
                setCampaigns(data.campaigns);
                setViewedAffiliateId(data?.affiliateId || null);
                setUnavailable(false);
            })
            .catch((e) => {
                // 450 = session (handled globally); anything else = the action is not deployed / not allowed → hide the block quietly.
                if (e?.response?.status !== 450) setUnavailable(true);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load, agencyView?.id]);

    const activeCount = useMemo(() => campaigns.filter((c) => !c.archivedAt).length, [campaigns]);
    const visible = useMemo(
        () => campaigns.filter((c) => showArchived || !c.archivedAt),
        [campaigns, showArchived]
    );

    const handleSubmit = (payload) => {
        setSubmitting(true);
        const isEdit = !!payload.code;
        ppapiPost(isEdit ? "updateCampaign" : "createCampaign", payload)
            .then((data) => {
                // Explicit echo required — never report success on a default payload.
                if (!data || typeof data.code !== "string") throw new Error("Campaigns are not enabled on this dashboard yet.");
                toast.success(isEdit ? "Campaign updated" : `Campaign created — code ${data.code}`);
                setShowForm(false); setEditing(null);
                load();
                if (!isEdit) setExpanded(data.code);
            })
            .catch((e) => toast.error(apiErrorMessage(e, "Could not save the campaign.")))
            .finally(() => setSubmitting(false));
    };

    const archive = (c) => {
        if (!window.confirm(`Archive “${c.label}”? Its links keep working but new orders are booked as Direct. History stays.`)) return;
        ppapiPost("archiveCampaign", { code: c.code })
            .then(() => { toast.success("Campaign archived"); load(); })
            .catch((e) => toast.error(apiErrorMessage(e, "Could not archive the campaign.")));
    };

    const cols = "minmax(200px,1.4fr) 110px 120px 110px 150px";

    return (
        <>
            <Helmet>
                <title>Referral &amp; Campaigns | Instaswap Partners</title>
            </Helmet>
            <div>
                <div className="secHdr" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h2 className="secTitle">Referral &amp; Campaigns</h2>
                        <p className="secLead">
                            Earn USDC by sharing your links. Every swap, split, payment, spot or perps trade made through
                            your link pays you a share of the fee — and you get referral points for every trader you bring.
                            Create one link per campaign to see which channel performs.
                        </p>
                    </div>
                    {(accountType === "kol" || accountType === "agency") && (
                        <Link to="/campaigns/stats" className="btnGhost sm" style={{ whiteSpace: "nowrap" }}>
                            <FontAwesomeIcon icon={faChartPie} /> Campaign stats
                        </Link>
                    )}
                </div>

                <div className="rfCols">
                    <div>
                        <h3 className="secH" style={{ marginTop: 0 }}>How it works</h3>
                        <div className="rfList">
                            <div className="rfItem">
                                <span className="n">1</span>
                                <div>
                                    <div className="t">Copy a link</div>
                                    <div className="d">Use the default link, or create a campaign to get a link with its own code.</div>
                                </div>
                            </div>
                            <div className="rfItem">
                                <span className="n">2</span>
                                <div>
                                    <div className="t">Share it</div>
                                    <div className="d">Each link works on the swap app, the trade app and the website.</div>
                                </div>
                            </div>
                            <div className="rfItem">
                                <span className="n">3</span>
                                <div>
                                    <div className="t">Earn USDC</div>
                                    <div className="d">
                                        Fees are shared on every order. Activate the 50/50 split on the{" "}
                                        <Link to="/revenue-share" style={{ color: "#3ad17e" }}>Revenue Share</Link> page to earn on swaps too.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="secH" style={{ marginTop: 0 }}>Good to know</h3>
                        <div className="panel">
                            <div style={{ paddingBottom: 16, borderBottom: "1px solid rgba(255,255,255,.06)" }}>
                                <div className="pTitle" style={{ fontSize: 13.5, fontFamily: "inherit" }}>First link wins · valid for 30 days</div>
                                <div className="pSub" style={{ marginTop: 5 }}>
                                    A user is yours from the first link he opens, for 30 days after his last order. Another partner's link
                                    does not take him over while that attribution is alive.
                                </div>
                            </div>
                            <div style={{ paddingTop: 16 }}>
                                <div className="pTitle" style={{ fontSize: 13.5, fontFamily: "inherit" }}>Campaign codes never change</div>
                                <div className="pSub" style={{ marginTop: 5 }}>
                                    Orders keep the code they were created with. Archive a campaign instead of deleting it — the history stays.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ fontWeight: 600, fontSize: 13.5, color: "#dfe6e0", margin: "28px 0 12px" }}>
                    Your default links{" "}
                    <span style={{ color: "#7c847e", fontWeight: 400 }}>· valid for 30 days · booked as “Direct” in stats</span>
                </div>
                <LinksBlock affiliateId={affiliateId} code={null} />

                {!unavailable && (
                    <>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "32px 0 12px", flexWrap: "wrap" }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5, color: "#dfe6e0" }}>
                                Campaigns{" "}
                                <span style={{ color: "#7c847e", fontWeight: 400 }}>· {activeCount} active of {MAX_ACTIVE}</span>
                            </div>
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                {campaigns.some((c) => c.archivedAt) && (
                                    <label style={{ fontSize: 12.5, color: "#aeb6b0", display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                                        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> show archived
                                    </label>
                                )}
                                {!readOnly && (
                                    <button type="button" className="btnPrimary sm" onClick={() => { setEditing(null); setShowForm(true); }} disabled={activeCount >= MAX_ACTIVE}>
                                        <FontAwesomeIcon icon={faPlus} /> New campaign
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="gtEmpty"><FontAwesomeIcon icon={faSpinner} spin /> Loading campaigns…</div>
                        ) : visible.length === 0 ? (
                            <div className="gtEmpty">
                                {readOnly ? "This partner has no campaigns yet." : "No campaigns yet. Create one to get a link with its own code and stats."}
                            </div>
                        ) : (
                            <div className="gtScroll">
                                <div style={{ minWidth: 720 }}>
                                    <div className="gtHead" style={{ gridTemplateColumns: cols }}>
                                        <span>Campaign</span><span>Code</span><span>Created</span><span>Status</span><span className="r">Actions</span>
                                    </div>
                                    {visible.map((c) => {
                                        const archived = !!c.archivedAt;
                                        const paused = !archived && Number(c.active) === 0;
                                        const open = expanded === c.code;
                                        return (
                                            <React.Fragment key={c.code}>
                                                <div className="gtRow" style={{ gridTemplateColumns: cols }}>
                                                    <span className="cellStrong">{c.label}</span>
                                                    <span className="cellMono">{c.code}</span>
                                                    <span>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}</span>
                                                    <span>
                                                        <span className={`pill ${archived ? "fail" : paused ? "pend" : "done"}`}>
                                                            <span className="pd" />{archived ? "Archived" : paused ? "Paused" : "Active"}
                                                        </span>
                                                    </span>
                                                    <span className="r" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                                        <button type="button" className={`iconBtn${open ? " edit" : ""}`} title="Links" onClick={() => setExpanded(open ? null : c.code)}>
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button>
                                                        <Link to={`/campaigns/stats?campaign=${encodeURIComponent(c.code)}`} className="iconBtn" title="Stats">
                                                            <FontAwesomeIcon icon={faChartPie} />
                                                        </Link>
                                                        {!readOnly && !archived && (
                                                            <>
                                                                <button type="button" className="iconBtn edit" title="Edit" onClick={() => { setEditing(c); setShowForm(true); }}>
                                                                    <FontAwesomeIcon icon={faPenToSquare} />
                                                                </button>
                                                                <button type="button" className="iconBtn danger" title="Archive" onClick={() => archive(c)}>
                                                                    <FontAwesomeIcon icon={faBoxArchive} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </span>
                                                </div>
                                                {open && (
                                                    <div style={{ padding: "12px 14px 18px", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
                                                        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "start" }}>
                                                            <LinksBlock affiliateId={affiliateId} code={c.code} compact />
                                                            <button type="button" className="btnGhost sm" onClick={() => setQr(buildLink(LINK_SURFACES[0], affiliateId, c.code))}>
                                                                <FontAwesomeIcon icon={faQrcode} /> QR
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {showForm && (
                    <Modal isOpen={showForm} onRequestClose={submitting ? undefined : () => { setShowForm(false); setEditing(null); }} style={modalStyles} ariaHideApp={false} contentLabel="Campaign form">
                        <CampaignForm initial={editing} onSubmit={handleSubmit} onCancel={() => { setShowForm(false); setEditing(null); }} submitting={submitting} />
                    </Modal>
                )}

                {qr && (
                    <Modal isOpen onRequestClose={() => setQr(null)} style={modalStyles} ariaHideApp={false} contentLabel="QR code">
                        <div className="iswModal" style={{ maxWidth: "none", textAlign: "center" }}>
                            <h2 className="mTitle">Scan to open</h2>
                            <div style={{ background: "#fff", display: "inline-block", padding: 12, borderRadius: 12, margin: "12px 0" }}>
                                <QRCodeSVG value={qr} size={220} />
                            </div>
                            <p className="fldHelp" style={{ wordBreak: "break-all" }}>{qr}</p>
                            <button type="button" className="btnGhost" onClick={() => setQr(null)}>Close</button>
                        </div>
                    </Modal>
                )}
            </div>
        </>
    );
};

export default Referral;
